// @flow

import React, { type Element } from 'react';
import { NativeTypes } from 'react-dnd-html5-backend';
import Csrf from 'app/common/lib/Csrf';
import i18n from 'app/common/lib/i18n';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { BasicImage } from 'app/packages/pinterest-flow-types/image';
import urlUtil from 'app/common/lib/urlUtil';
import { track } from 'app/common/lib/react/analytics';
import DropTargetBox, { type VideoType } from 'app/common/react/components/DragDrop/DropTargetBox';
import { Box, Text, Flyout } from 'gestalt';

const MAX_UPLOAD_SIZE = 2 * 1000 * 1000 * 1000; // 2 jigabites
const CHECK_FREQUENCY = 1000; // Check once per 5 seconds
const VIDEO_TYPES = ['video/quicktime', 'video/mp4'];

type Props = {
  allowReset?: boolean,
  allowVideo?: boolean,
  display: 'image' | 'pin' | 'communityImage' | 'communityCoverImage',
  existingImageSrc?: ?string,
  idealErrorDirection: 'up' | 'right' | 'down' | 'left',
  height?: number,
  onImageUploadStart: () => void,
  onImageUploadFinished: ({ image?: BasicImage, video?: VideoType }) => void,
  onReset?: () => void,
  width?: number,
  uploadSource?: string,
};

type State = {
  file: ?string,
  imagePreviewSrc: ?string | ?ArrayBuffer,
  errorFlyout: ?Element<any>,
  isVideo: boolean,
  isVideoComplete: boolean,
  thumbnail: ?string,
  videoUrls: ?Object,
  uploadId: ?string,
  progress: number,
};

const isSuccess = (status) => status >= 200 && status < 400;

class DropTargetContainer extends React.Component<Props, State> {
  static defaultProps = {
    onImageUploadStart: () => {},
  };

  state = {
    file: null,
    imagePreviewSrc: this.props.existingImageSrc || '',
    isVideo: false,
    isVideoComplete: false,
    thumbnail: null,
    videoUrls: null,
    uploadId: null,
    errorFlyout: null,
    progress: 0,
  };

  componentDidMount() {
    this.mounted = true;

    this.fileReader = new FileReader();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  mounted = false;

  containerRef: ?HTMLElement = null;
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  fileReader: * = null;
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  file: * = null;
  fileInputRef: ?HTMLInputElement = null;
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  xhr: * = null;
  CHECK_VIDEO_TIMEOUT: ?TimeoutID = null;
  ERROR_MESSAGE: string = i18n._(
    'Unable to upload video. Please try again later.',
    "Error shown when we can't accept video uploads",
  );

  handleFileDrop = (item: any, monitor: any) => {
    // Only handle a file when the upload is not in progress
    if (monitor && (!this.xhr || this.xhr.readyState === 4)) {
      const droppedFiles = monitor.getItem().files;
      const file = droppedFiles[0];
      this.handleFile(file);
    }
  };

  handleFile = (file: Blob) => {
    const { onImageUploadStart } = this.props;
    if (file.size > MAX_UPLOAD_SIZE) {
      this.showError(
        i18n._(
          'The video is too large. The maximum size is 2 GB.',
          'Error if a user tries to upload a video that is too large',
        ),
      );
    } else if (this.props.allowVideo && VIDEO_TYPES.includes(file.type)) {
      this.loadVideo(file);
      onImageUploadStart();
    } else {
      this.fileReader.onload = () => this.uploadImage(file);
      this.fileReader.readAsDataURL(file);
      onImageUploadStart();
    }
  };

  showError = (message: string) => {
    const errorFlyout = (
      <Flyout
        color="red"
        anchor={this.containerRef}
        idealDirection={this.props.idealErrorDirection}
        onDismiss={() => this.setState({ errorFlyout: null })}
        size="sm"
        shouldFocus={false}
      >
        <Box padding={3}>
          <Text color="white" weight="bold">
            <span>{message}</span>
          </Text>
        </Box>
      </Flyout>
    );
    this.removePreview(errorFlyout);
  };

  openFileDialog = () => {
    if (!this.state.imagePreviewSrc && !this.state.isVideo) {
      track(PCONST.thrift.EventType.CLICK, {
        component: PCONST.thrift.ComponentType.DID_IT_MODAL_FULL_SHEET,
        element: PCONST.thrift.ElementType.TAKE_PHOTO_BUTTON,
      });
      if (this.fileInputRef) {
        this.fileInputRef.click();
      }
    }
  };

  /* Handler for file input */
  openFile = (event: any) => {
    const input = event.target;
    const file = input.files[0];
    this.handleFile(file);
  };

  removePreview = (errorFlyout: ?Element<any>) => {
    this.setState({
      imagePreviewSrc: '',
      progress: 0,
      isVideo: false,
      isVideoComplete: false,
      videoUrls: null,
      errorFlyout,
    });
    if (this.fileInputRef) {
      this.fileInputRef.value = '';
    }
    if (this.xhr && this.xhr.abort) {
      this.xhr.abort();
      this.xhr = null;
    }
    if (this.CHECK_VIDEO_TIMEOUT) {
      clearTimeout(this.CHECK_VIDEO_TIMEOUT);
    }
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  loadVideo = (file: Blob) => {
    this.setState({ isVideo: true });

    ResourceFactory.create('VIPResource', { type: 'video' })
      .callCreate()
      .then(
        (response) => {
          const { upload_id, upload_url, upload_parameters } = response.resource_response.data;
          this.setState({ uploadId: upload_id });
          this.uploadVideo(file, upload_id, upload_url, upload_parameters);
        },
        () => {
          this.showError(this.ERROR_MESSAGE);
        },
      );
  };

  uploadVideo(file: Blob, uploadId: string, uploadUrl: string, uploadParams: Object) {
    // Open AJAX request
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl, true);

    xhr.onload = () => {
      if (isSuccess(xhr.status)) {
        this.setState({ errorFlyout: null, progress: 100, isVideoComplete: true });
      } else {
        this.showError(
          i18n._('Something went wrong. Please try again later.', 'Error if video fails to upload'),
        );
      }
      this.checkForVideo(uploadId);
    };

    // Watch upload progress
    xhr.upload.onprogress = (event: any) => {
      const progress = Math.round((100 * event.loaded) / event.total);
      this.setState({ progress });
    };

    const uploadData = new FormData();
    for (const key in uploadParams) {
      uploadData.append(key, uploadParams[key]);
    }
    uploadData.append('file', file);
    xhr.send(uploadData);
    this.xhr = xhr;
  }

  checkForVideo = (uploadId: string) => {
    ResourceFactory.create('VIPResource', { upload_ids: [uploadId] })
      .callGet()
      .then((response) => {
        if (
          response.resource_response &&
          response.resource_response.data &&
          response.resource_response.data[uploadId]
        ) {
          const data = response.resource_response.data[uploadId];
          const { status, signature } = data;
          if (status === 'processing' || status === 'registered') {
            this.CHECK_VIDEO_TIMEOUT = setTimeout(
              () => this.checkForVideo(uploadId),
              CHECK_FREQUENCY,
            );
          } else if (status === 'succeeded') {
            ResourceFactory.create('VideoUrlsResource', { signatures: [signature] })
              .callGet()
              .then((response) => this.handleVideoUploadSuccess(response, signature, uploadId));
          } else {
            this.showError(this.ERROR_MESSAGE);
          }
        } else {
          this.showError(this.ERROR_MESSAGE);
        }
      });
  };

  handleVideoUploadSuccess(response: Object, signature: string, uploadId: string) {
    if (!this.mounted) {
      return;
    }

    const data = response.resource_response.data;
    const thumbnail = `https://i.pinimg.com/${data[signature].metadata.thumbnail}`;
    const videoUrls = data[signature].urls;
    this.setState({ videoUrls, thumbnail });

    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const reader = new FileReader();
      reader.onloadend = () => {
        this.props.onImageUploadFinished({
          video: {
            uploadId,
            imageBase64: reader.result,
          },
        });
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', thumbnail);
    xhr.responseType = 'blob';
    xhr.send();
  }

  uploadImage = (file: Blob) => {
    this.setState({ imagePreviewSrc: this.fileReader.result, isVideo: false });

    const uploadUrl = urlUtil.absolutify('/upload-image/');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl, true);
    xhr.setRequestHeader('X-CSRFToken', Csrf.getCSRFToken());
    xhr.setRequestHeader('X-UPLOAD-SOURCE', this.props.uploadSource || 'unknown');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = (evt) => {
      const progress = Math.round((100 * evt.loaded) / evt.total);
      this.setState({ progress });
    };
    xhr.onload = () => {
      if (isSuccess(xhr.status)) {
        const response = JSON.parse(xhr.responseText);
        if (response.success) {
          this.props.onImageUploadFinished({
            image: {
              height: response.height,
              url: response.image_url,
              width: response.width,
            },
          });
          this.setState({ errorFlyout: null });
        } else {
          this.showError(response.error);
        }
      }
    };
    const uploadData = new FormData();
    // Flow can't handle a type passed to this function that is typed as null | File
    uploadData.append('img', file);
    xhr.send(uploadData);
    this.xhr = xhr;
  };

  render() {
    const { FILE } = NativeTypes;
    const {
      errorFlyout,
      imagePreviewSrc,
      isVideo,
      isVideoComplete,
      progress,
      thumbnail,
      videoUrls,
    } = this.state;
    const { allowReset, allowVideo, display, existingImageSrc, height, width } = this.props;

    const imageSrc = imagePreviewSrc || existingImageSrc;
    const accepts = allowVideo ? 'image/*,video/mp4,video/quicktime' : 'image/*';

    return (
      <div
        ref={(c) => {
          this.containerRef = c;
        }}
      >
        <Box display="visuallyHidden">
          <input
            accept={accepts}
            onChange={this.openFile}
            ref={(c) => {
              this.fileInputRef = c;
            }}
            type="file"
          />
        </Box>
        {/* $FlowFixMe: new errors found from flow 0.96 upgrade */}
        <DropTargetBox
          allowReset={allowReset}
          onClick={this.openFileDialog}
          handleRemoveClick={this.removePreview}
          imagePreviewSrc={imageSrc}
          accepts={[FILE]}
          onDrop={this.handleFileDrop}
          height={height}
          width={width}
          progress={progress}
          isVideo={isVideo}
          isVideoComplete={isVideoComplete}
          videoUrls={videoUrls}
          thumbnail={thumbnail}
          display={display}
        />
        {errorFlyout}
      </div>
    );
  }
}

export default DropTargetContainer;
